import { getCurrentCsrfCookie } from '../../../../../application/csrfProtection/getCurrentCsrfCookie';
import { ILocalization, ILocalizationToken } from '../../../../../application/language/ILocalization';
import { inject } from '../../../../../common/container/inject';
import { IError } from '../../../../../common/error/IError';
import { IValidationErrorData } from '../../../../../common/error/IValidationErrorData';
import { Store } from '../../../../../common/store/base/Store';
import { WrongCredentialError } from '../../../../../service/public/activeUser/error/WrongCredentialError';
import { IRootPublicService, RootPublicServiceToken } from '../../../../../service/public/RootPublicService';
import { getSafeRedirectParam } from '../../../../../service/route/authRedirect/getSafeRedirectParam';
import { IRouterService, RouterServiceToken } from '../../../../../service/route/IRouterService';
import { ISignInPageDomain } from './ISignInPageDomain';
import { SignInPageUIStore } from './SignInPageUiStore';

const POST_LOGIN_REDIRECT_KEY = 'post_login_redirect';

export class SignInPageDomain extends Store implements ISignInPageDomain {
  constructor(
    public ui = new SignInPageUIStore(),
    public i18n: ILocalization = inject<ILocalization>(ILocalizationToken),
    private publicRootService: IRootPublicService = inject<IRootPublicService>(RootPublicServiceToken),
    private router: IRouterService = inject<IRouterService>(RouterServiceToken),
  ) {
    super();
  }

  async boot() {
    await this.checkCurrentUser();
    await this.loadAdServers();
    await this.loadSSOProviders();
  }

  async checkCurrentUser() {
    const activeCsrfCookie = getCurrentCsrfCookie();
    if (activeCsrfCookie !== undefined && activeCsrfCookie !== null && activeCsrfCookie !== '') {
      this.router.goTo('/');
    }
  }

  async loadAdServers(): Promise<void> {
    const adServers = await this.publicRootService.activeDirectory.server.search({
      limit: 1000,
      filter: { isActive: true },
    });

    const mainAd = adServers.data.find((adServer) => adServer.isMainLoginMethod === true);

    if (mainAd) {
      this.ui.loginMethodValue.setValue(mainAd.id);
    }

    this.ui.loginMethods.setList([
      { name: this.i18n.translate('login.basic'), value: null },
      ...adServers.data.map((server) => ({ name: server.name, value: server.id })),
    ]);
  }

  loadSSOProviders = async (): Promise<void> => {
    this.ui.isSSOLoading.setValue(true);

    try {
      const res = await this.publicRootService.sso.publicSearch({
        limit: 100000,
        filter: [{ fieldName: 'isActive', type: 'equal', value: true }],
      });
      this.ui.ssoProviders.setList(res || []);
    } finally {
      this.ui.isSSOLoading.setValue(false);
    }
  };

  startSSOLogin = async (providerId: string): Promise<void> => {
    this.ui.isLockForm.setValue(true);

    try {
      const safe = await getSafeRedirectParam()
        .then((u) => (u?.pathname ? u.pathname + (u.search || '') : '/'))
        .catch(() => '/');

      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, safe);

      const { redirectUrl } = await this.publicRootService.ssoAuth.startLogin(providerId);
      window.location.assign(redirectUrl);
    } finally {
      this.ui.isLockForm.setValue(false);
    }
  };

  login = async (): Promise<boolean> => {
    this.clearValidationErrors();
    this.ui.isLockForm.setValue(true);

    const login = this.ui.loginField.value;
    const password = this.ui.passwordField.value;
    const loginMethodValue = this.ui.loginMethodValue.value;

    const isLogged = await this.publicRootService.activeUser
      .loginUserByPassword(login, password, loginMethodValue)
      .catch(this.handleLoginUserByPasswordError);

    this.ui.isLockForm.setValue(false);

    if (isLogged) {
      this.redirectAfterAuth();
    }

    return isLogged;
  };

  logout = async (): Promise<boolean> => {
    await this.publicRootService.activeUser.logoutActiveUser();
    this.ui.loginField.setValue('');
    this.ui.passwordField.setValue('');
    this.router.goTo('/login');
    return true;
  };

  changeLoginMethod(value: any): void {
    this.ui.loginMethodValue.setValue(value);
  }

  private handleLoginUserByPasswordError = (error: IError): boolean => {
    if (error instanceof WrongCredentialError) {
      this.setWrongCredentialError(error.data);
    }
    return false;
  };

  private clearValidationErrors() {
    this.ui.isError.setValue(false);
    this.ui.loginErrorMessage.setValue('');
    this.ui.passwordErrorMessage.setValue('');
  }

  private setWrongCredentialError = (validationErrorData: IValidationErrorData[]) => {
    this.ui.isError.setValue(true);
    const loginError = validationErrorData.find((validationError) => validationError.target === 'login');
    const passwordError = validationErrorData.find((validationError) => validationError.target === 'password');
    const commonErrorMessage = loginError?.message || passwordError?.message || this.i18n.translate('login.incorrectValid');

    this.ui.loginErrorMessage.setValue(loginError?.message || '');
    this.ui.passwordErrorMessage.setValue(passwordError?.message || commonErrorMessage);
    this.ui.isLockForm.setValue(false);
  };

  private async redirectAfterAuth() {
    const fromSession = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);

    if (fromSession && fromSession.startsWith('/') && !fromSession.startsWith('//')) {
      this.router.goTo(fromSession);
      return;
    }

    const redirectUrl = await getSafeRedirectParam()
      .then((u) => (u?.pathname ? u.pathname + (u.search || '') : '/'))
      .catch(() => '/');

    this.router.goTo(redirectUrl);
  }
}
